import { create } from 'zustand';
import { approvalWorkflowService } from '../services/approval-workflow-service';
import type {
  ApprovalWorkflowConfig,
  ApprovalRule,
  ApprovalMode,
  CreateRulePayload,
  PasoAprobadorPedido,
} from '../types/approval-workflow.types';

interface ApprovalWorkflowState {
  config: ApprovalWorkflowConfig | null;
  isLoading: boolean;
  error: string | null;
  fetchConfig: () => Promise<void>;
  updateMode: (mode: ApprovalMode) => Promise<void>;
  updateAcumular: (acumular: boolean) => Promise<void>;
  addRule: (data: CreateRulePayload) => Promise<ApprovalRule>;
  updateRule: (id: string, data: Partial<CreateRulePayload>) => Promise<void>;
  deleteRule: (id: string) => Promise<void>;
  toggleRule: (id: string, isActive: boolean) => Promise<void>;
  setRuleApprovers: (ruleId: string, approvers: PasoAprobadorPedido[]) => Promise<void>;
}

export const useApprovalWorkflowStore = create<ApprovalWorkflowState>((set, get) => ({
  config: null,
  isLoading: false,
  error: null,

  fetchConfig: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await approvalWorkflowService.getConfig();
      set({ config: data, isLoading: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando configuración',
        isLoading: false,
      });
    }
  },

  updateMode: async (mode) => {
    set({ error: null });
    try {
      const data = await approvalWorkflowService.updateMode(mode);
      set({ config: data });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando modo' });
      throw err;
    }
  },

  updateAcumular: async (acumular) => {
    set({ error: null });
    try {
      const modo = get().config?.defaultMode ?? 'AUTO_APPROVE_IF_MARGINS_MET';
      const data = await approvalWorkflowService.updateAcumular(modo, acumular);
      set({ config: data });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando la configuración' });
      throw err;
    }
  },

  // Devuelve la regla creada: hace falta su id para asignarle la cadena de
  // aprobadores en el mismo paso, sin obligar a abrir otro diálogo después.
  addRule: async (data) => {
    set({ error: null });
    try {
      const creada = await approvalWorkflowService.addRule(data);
      await get().fetchConfig();
      return creada;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando regla' });
      throw err;
    }
  },

  updateRule: async (id, data) => {
    set({ error: null });
    try {
      await approvalWorkflowService.updateRule(id, data);
      await get().fetchConfig();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando regla' });
      throw err;
    }
  },

  deleteRule: async (id) => {
    try {
      await approvalWorkflowService.deleteRule(id);
      set((s) => ({
        config: s.config ? { ...s.config, rules: s.config.rules.filter((r) => r.id !== id) } : null,
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando regla' });
    }
  },

  toggleRule: async (id, isActive) => {
    try {
      await approvalWorkflowService.updateRule(id, { isActive });
      set((s) => ({
        config: s.config
          ? {
              ...s.config,
              rules: s.config.rules.map((r) => (r.id === id ? { ...r, isActive } : r)),
            }
          : null,
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando regla' });
    }
  },

  setRuleApprovers: async (ruleId, approvers) => {
    set({ error: null });
    try {
      await approvalWorkflowService.setRuleApprovers(ruleId, approvers);
      await get().fetchConfig();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error asignando aprobadores' });
      throw err;
    }
  },
}));
